import {
  Assignment as AssignmentIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { TeacherCard } from '../../lib/components/TeacherCard';
import { useTeacherStore } from '../../stores/teacherStore';
import { StaffRole } from '../../types';

export const StaffRoles: React.FC = () => {
  const { teachers, assignTeacherToRole, unassignTeacherFromRole, getStaffRolesByLevel, getTeacher } =
    useTeacherStore();

  const [selectedLevel, setSelectedLevel] = useState<StaffRole['level']>('SLT');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [viewTeacherDialogOpen, setViewTeacherDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<StaffRole | null>(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>('');

  const levels: StaffRole['level'][] = ['SLT', 'HoD', 'HoY'];

  const levelLabels: Record<StaffRole['level'], string> = {
    'Head Teacher': 'Head Teacher',
    SLT: 'Senior Leadership Team',
    HoD: 'Heads of Department',
    HoY: 'Heads of Year',
    Teacher: 'Teachers',
  };

  const levelColors: Record<StaffRole['level'], 'primary' | 'secondary' | 'info' | 'success' | 'default'> = {
    'Head Teacher': 'primary',
    SLT: 'primary',
    HoD: 'secondary',
    HoY: 'info',
    Teacher: 'default',
  };

  const handleAssignClick = (role: StaffRole) => {
    setSelectedRole(role);
    setSelectedTeacherId('');
    setAssignDialogOpen(true);
  };

  const handleUnassign = (role: StaffRole) => {
    if (role.assignedTeacherId) {
      unassignTeacherFromRole(role.id);
    }
  };

  const handleViewTeacher = (role: StaffRole) => {
    if (role.assignedTeacherId) {
      setSelectedRole(role);
      setViewTeacherDialogOpen(true);
    }
  };

  const handleConfirmAssign = () => {
    if (selectedRole && selectedTeacherId) {
      assignTeacherToRole(selectedRole.id, selectedTeacherId);
      setAssignDialogOpen(false);
      setSelectedRole(null);
      setSelectedTeacherId('');
    }
  };

  const getAssignedTeacher = (role: StaffRole) => {
    if (!role.assignedTeacherId) return null;
    return getTeacher(role.assignedTeacherId);
  };

  const getUnassignedRolesCount = (level: StaffRole['level']) => {
    return getStaffRolesByLevel(level).filter((r) => !r.assignedTeacherId).length;
  };

  const filteredRoles = getStaffRolesByLevel(selectedLevel);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Staff Roles & Responsibilities
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage leadership roles and responsibilities across the school
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {levels.map((level) => {
          const levelRoles = getStaffRolesByLevel(level);
          const unassigned = getUnassignedRolesCount(level);
          return (
            <Grid item xs={12} sm={6} md={4} key={level}>
              <Paper
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: 2,
                  borderColor: selectedLevel === level ? 'primary.main' : 'transparent',
                  '&:hover': {
                    borderColor: 'primary.light',
                  },
                }}
                onClick={() => setSelectedLevel(level)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {levelLabels[level]}
                    </Typography>
                    <Typography variant="h5">
                      {levelRoles.length - unassigned}/{levelRoles.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {unassigned > 0 ? `${unassigned} unassigned` : 'All assigned'}
                    </Typography>
                  </Box>
                  <AssignmentIcon color={selectedLevel === level ? 'primary' : 'action'} sx={{ fontSize: 40 }} />
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Role Level Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={selectedLevel} onChange={(_, newValue) => setSelectedLevel(newValue)} variant="fullWidth">
          {levels.map((level) => (
            <Tab key={level} label={levelLabels[level]} value={level} />
          ))}
        </Tabs>
      </Paper>

      {/* Roles Grid */}
      <Grid container spacing={2}>
        {filteredRoles.map((role) => {
          const assignedTeacher = getAssignedTeacher(role);
          return (
            <Grid item xs={12} md={6} lg={4} key={role.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {role.title}
                      </Typography>
                      <Chip label={role.level} size="small" color={levelColors[role.level]} />
                    </Box>
                    {assignedTeacher && <Chip label="Assigned" size="small" color="success" variant="outlined" />}
                  </Box>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    {role.description}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    Key Responsibilities:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mt: 1, mb: 2 }}>
                    {role.responsibilities.slice(0, 3).map((resp, index) => (
                      <Typography component="li" variant="caption" color="text.secondary" key={index} sx={{ mb: 0.5 }}>
                        {resp}
                      </Typography>
                    ))}
                    {role.responsibilities.length > 3 && (
                      <Typography variant="caption" color="text.secondary">
                        +{role.responsibilities.length - 3} more...
                      </Typography>
                    )}
                  </Box>

                  {assignedTeacher && (
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: 'success.50',
                        borderRadius: 1,
                        cursor: 'pointer',
                      }}
                      onClick={() => handleViewTeacher(role)}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Assigned to:
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {assignedTeacher.firstName} {assignedTeacher.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {assignedTeacher.email}
                      </Typography>
                    </Box>
                  )}

                  {!assignedTeacher && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="caption">This role is currently unassigned</Typography>
                    </Alert>
                  )}
                </CardContent>

                <CardActions>
                  {assignedTeacher ? (
                    <>
                      <Button
                        size="small"
                        startIcon={<PersonRemoveIcon />}
                        color="error"
                        onClick={() => handleUnassign(role)}
                      >
                        Unassign
                      </Button>
                      <Button size="small" onClick={() => handleViewTeacher(role)}>
                        View Teacher
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="small"
                      startIcon={<PersonAddIcon />}
                      onClick={() => handleAssignClick(role)}
                      variant="contained"
                    >
                      Assign Teacher
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Assign Teacher Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Teacher to {selectedRole?.title}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              Select a teacher to assign to this role:
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Select Teacher</InputLabel>
              <Select
                value={selectedTeacherId}
                onChange={(e) => setSelectedTeacherId(e.target.value)}
                label="Select Teacher"
              >
                {teachers.map((teacher) => (
                  <MenuItem key={teacher.id} value={teacher.id}>
                    <Box>
                      <Typography variant="body2">
                        {teacher.firstName} {teacher.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {teacher.subjects.join(', ')} • {teacher.contractType}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmAssign} variant="contained" disabled={!selectedTeacherId}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Teacher Dialog */}
      <Dialog open={viewTeacherDialogOpen} onClose={() => setViewTeacherDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedRole?.title} - Teacher Details</DialogTitle>
        <DialogContent>
          {selectedRole?.assignedTeacherId &&
            (() => {
              const teacher = getTeacher(selectedRole.assignedTeacherId);
              return teacher ? (
                <Box sx={{ pt: 2 }}>
                  <TeacherCard teacher={teacher} showAvailability={true} />
                </Box>
              ) : null;
            })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewTeacherDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StaffRoles;
